import { Component, Input, OnInit } from '@angular/core';
import { ThreeDService } from '../services/three-d.service';
import { ConfigurationService } from '../services/configuration.service';
import { TranslateService } from '@ngx-translate/core';
import { Purpose } from '../enums/purpose';
import { NotificationService } from '../services/notification.service';
import { FloorTypes } from '../enums/floorTypes.enum';
import { PdfGenerationService, StepItem } from '../services/pdf-generation.service';
import { DoorType } from '../enums/doorType.enum';
import { DoorDirection } from '../enums/doorDirection.enum';
import { WallFinish } from '../models/wallFinish.model';
import { WallSide } from '../enums/wallSide.enum';
import { LanguageService } from '../services/language.service';
import { DecimalPipe } from '@angular/common';
import { environment } from 'src/environments/environment';
import { ModularGoogleAnalyticsService } from '../services/modular-google-analytics.service';
import { InnerWall } from '../models/innerWall.model';
import { InnerWallType } from '../enums/innerWallType.enum';
import { HingeType } from '../enums/hingeType.enum';
import { DoorHandleType } from '../enums/doorHandleType.enum';
import { CollisionPoint } from '../models/collisionPoint.model';
import { StartupService } from '../services/startup.service';
import { Conflicts } from '../models/conflicts.model';
import { DoorPosition } from '../models/doorPosition.model';

@Component({
  selector: 'app-summary',
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.scss'],
})
export class SummaryComponent implements OnInit {
  @Input() readOnly = true;
  @Input() loading = false;
  imageSrc: string;
  decimalPipe: DecimalPipe;
  innerWallTypes = InnerWallType;
  hingeType = HingeType;
  doorHandleType = DoorHandleType;
  doorType = DoorType;
  wallSide = WallSide;
  conflicts: Conflicts;
  loading2d = false;
  loadingPDF: boolean;

  constructor(
    private threeDService: ThreeDService,
    private configurationService: ConfigurationService,
    private translateService: TranslateService,
    private notificationService: NotificationService,
    private pdfGenerationService: PdfGenerationService,
    private languageService: LanguageService,
    private modularGoogleAnalyticsService: ModularGoogleAnalyticsService,
    private startupService: StartupService
  ) {
    this.decimalPipe = new DecimalPipe(this.currentLocale);
  }

  get hasToken() {
    return !!this.startupService.token;
  }

  get currentLocale() {
    return this.languageService.currentLanguage;
  }

  get width(): number {
    return this.configurationService.width;
  }

  get length(): number {
    return this.configurationService.length;
  }

  get height(): number {
    return this.configurationService.height;
  }

  get widthExternal(): number {
    return this.configurationService.widthExternal;
  }

  get lengthExternal(): number {
    return this.configurationService.lengthExternal;
  }

  get heightExternal(): number {
    return this.configurationService.heightExternal;
  }

  get purpose(): string {
    const purpose = this.configurationService.purpose;
    switch (purpose) {
      case Purpose.Both:
        return this.translateService.instant('STEP1.PURPOSE_COOLINGFREEZING');
      case Purpose.Cooling:
        return this.translateService.instant('STEP1.PURPOSE_COOLING');
      case Purpose.Freezing:
        return this.translateService.instant('STEP1.PURPOSE_FREEZING');
      default:
        return '?';
    }
  }

  get wallThickness(): string {
    return this.configurationService.wallThickness + ' mm';
  }

  get floorInCoolingCell() {
    return this.configurationService.floorType !== FloorTypes.NONE;
  }

  get floorInCell(): string {
    return this.translatedYesNo(this.floorInCoolingCell);
  }

  get floorReinforcement(): string {
    const floorType = this.configurationService.floorType;
    if (!this.configurationService.allowedFloorTypesRecord) {
      return '?';
    }
    const selectedFloor = this.configurationService.allowedFloorTypesRecord[floorType];
    return this.translatedYesNo(selectedFloor ? selectedFloor.strong : false);
  }

  get floorType(): string {
    return this.translateService.instant('FLOORTYPES.' + this.configurationService.floorType.toString());
  }

  get plinthsInside(): string {
    return this.translatedYesNo(this.configurationService.plinthsInside);
  }

  get plinthsOutside(): string {
    return this.translatedYesNo(this.configurationService.plinthsOutside);
  }

  get sunkenFloor(): string {
    return this.translatedYesNo(this.configurationService.sunkenFloor);
  }

  get ventilationProfiles(): string {
    return this.translatedYesNo(this.configurationService.ventilationProfiles);
  }

  get ceilingMoldings(): string {
    return this.translatedYesNo(this.configurationService.ceilingMoldings);
  }

  get configurationIdExists(): boolean {
    return !!this.configurationService.configuration.configurationId;
  }

  get innerWalls(): InnerWall[] {
    return this.configurationService.configuration.settings.innerWalls;
  }

  ngOnInit() {
    if (!this.readOnly) {
      this.imageSrc = this.threeDService.getMainScreenshot();
    }
    this.conflicts = this.threeDService.getConflicts();
  }

  doorTypeToString(doorType: DoorType) {
    switch (doorType) {
      case DoorType.Slide:
        return this.translateService.instant('STEP5.SLIDE');
      case DoorType.Turn:
        return this.translateService.instant('STEP5.TURN');
    }
  }

  doors() {
    const doorsettings = this.configurationService.configuration.settings.doorSettings.sort((a, b) => {
      if (a.side > b.side) {
        return 1;
      }
      if (a.side < b.side) {
        return -1;
      }
      if (a.left < b.left) {
        return -1;
      }
      if (a.left > b.left) {
        return 1;
      }
      return 0;
    });
    return doorsettings;
  }

  wallFinishes() {
    const wallAndCeilingFinishes = [
      this.configurationService.configuration.settings.wallFinishes[0],
      this.configurationService.configuration.settings.wallFinishes[1],
      this.configurationService.configuration.settings.wallFinishes[2],
      this.configurationService.configuration.settings.wallFinishes[3],
      this.configurationService.configuration.settings.wallFinishes[5],
    ];

    return wallAndCeilingFinishes;
  }

  directionToString(direction: DoorDirection) {
    if (direction === DoorDirection.left) {
      return 'LEFT';
    }
    return 'RIGHT';
  }

  translatedYesNo(value: boolean): string {
    if (value) {
      return this.translateService.instant('YES');
    }
    return this.translateService.instant('NO');
  }

  numberToString(value: number): string {
    return this.decimalPipe.transform(value, '1.0-0', this.currentLocale);
  }

  shareLink() {
    this.modularGoogleAnalyticsService.sendEvent('summary', 'shareLink');
    const link = environment.frontendUrl + '/?share=' + this.configurationService.configuration.configurationId;
    const selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.left = '0';
    selBox.style.top = '0';
    selBox.style.opacity = '0';
    selBox.value = link;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);
    this.notificationService.showNotification('NOTIFICATIONS.LINKCOPIED', NotificationService.DEFAULT_TIMEOUT);
  }

  async createPDF() {
    this.loadingPDF = true;
    this.modularGoogleAnalyticsService.sendEvent('summary', 'pdf');
    const doc = await this.pdfGenerationService.createPDF();
    const image = this.threeDService.getMainScreenshot(true);
    const imageInside = this.threeDService.getMainScreenshot(true, true);
    const doorImages = this.threeDService.getScreenshotsForSidesWithDoors();
    this.pdfGenerationService.generateHeader(doc, image, !this.startupService.token);
    let height = 15;
    const marginBetweenSteps = 2;

    if (!!this.conflicts && (this.conflicts.collisions.length > 0 || this.conflicts.invalidPanels.length > 0)) {
      height = this.pdfGenerationService.generateStep(
        doc,
        null,
        this.getCollisionsForPdf(),
        height + marginBetweenSteps,
        'CONFLICTS',
        true
      );
    }

    height = this.pdfGenerationService.generateStep(
      doc,
      null,
      [
        { title: 'STEP1.PURPOSE', value: this.purpose },
        { title: 'STEP2.WALLTHICKNESS', value: this.wallThickness },
        {
          title: 'STEP3.SIZEINTERNAL',
          value:
            this.numberToString(this.length) +
            ' mm x ' +
            this.numberToString(this.width) +
            ' mm x ' +
            this.numberToString(this.height) +
            ' mm',
        },
        {
          title: 'STEP3.SIZEEXTERNAL',
          value:
            this.numberToString(this.lengthExternal) +
            ' mm x ' +
            this.numberToString(this.widthExternal) +
            ' mm x ' +
            this.numberToString(this.heightExternal) +
            ' mm',
        },
      ],
      height + marginBetweenSteps,
      'SUMMARY.CELLPROPERTIES'
    );

    if (this.floorInCoolingCell) {
      height = this.pdfGenerationService.generateStep(
        doc,
        null,
        [
          { title: 'SUMMARY.FLOORINCELL', value: this.floorInCell },
          { title: 'SUMMARY.FLOORTYPE', value: this.floorType },
          { title: 'STEP4.SUNKENFLOOR', value: this.sunkenFloor },
          { title: 'STEP4.PLINTHSINSIDE', value: this.plinthsInside },
          { title: 'STEP4.PROFILES', value: this.ventilationProfiles },
        ],
        height + marginBetweenSteps,
        'STEP4.SUMMARYTITLE'
      );
    } else {
      height = this.pdfGenerationService.generateStep(
        doc,
        4,
        [{ title: 'SUMMARY.FLOORINCELL', value: this.floorInCell }],
        height + marginBetweenSteps,
        'STEP4.SUMMARYTITLE'
      );
    }
    const doorsInPDF = this.doors().map(door => {
      let value =
        this.doorTypeToString(door.doorModel.type) +
        ', ' +
        this.translateService.instant('STEP5.' + this.directionToString(door.direction)) +
        '\n' +
        this.translateService.instant('SUMMARY.CLEAROPENING') +
        ': ' +
        this.numberToString(door.doorModel.openingWidth) +
        ' mm x ' +
        this.numberToString(door.doorModel.openingHeight) +
        ' mm\n' +
        this.numberToString(door.left) +
        ' mm ' +
        this.translateService.instant('SUMMARY.FROMLEFTCORNER') +
        '\n';
      if (door.doorModel.type === DoorType.Turn) {
        value +=
          this.translateService.instant('DOORHINGE.HINGE') +
          ': ' +
          this.translateService.instant(('DOORHINGE.' + door.hingeType).toUpperCase()) +
          ', ' +
          this.translateService.instant('DOORHANDLE.HANDLE') +
          ': ' +
          this.translateService.instant(('DOORHANDLE.' + door.doorHandleType).toUpperCase()) +
          '\n';
      }
      value +=
        this.translateService.instant('STEP6.FINISH') +
        ' ' +
        this.translateService.instant('STEP6.OUTSIDESMALL') +
        ': ' +
        this.translateService.instant(('FINISHTYPES1.' + door.leafExteriorFinish).toUpperCase()) +
        ', ' +
        this.translateService.instant(('FINISHTYPES2.' + door.leafExteriorFinish).toUpperCase()) +
        '\n';
      value +=
        this.translateService.instant('STEP6.FINISH') +
        ' ' +
        this.translateService.instant('STEP6.INSIDESMALL') +
        ': ' +
        this.translateService.instant(('FINISHTYPES1.' + door.leafInteriorFinish).toUpperCase()) +
        ', ' +
        this.translateService.instant(('FINISHTYPES2.' + door.leafInteriorFinish).toUpperCase());
      let title = this.translateService.instant('WALL.' + door.side);
      if (door.innerWallId != null) {
        title += ' ' + this.getId(door.innerWallId);
      }
      title += ' (' + this.numberToString(this.getWidthOfWall(door)) + ' mm)';
      return {
        title,
        value,
      } as StepItem;
    });
    height = this.pdfGenerationService.generateStep(
      doc,
      null,
      doorsInPDF,
      height + marginBetweenSteps,
      'STEP5.SUMMARYTITLE'
    );

    if (this.innerWalls.length > 0) {
      const innerWallsInPdf = this.innerWalls.map((innerWall: InnerWall) => {
        const innerWallType = 'INNERWALL.' + InnerWallType[innerWall.type];
        return {
          title:
            this.translateService.instant('WALL.4') +
            ' ' +
            this.getId(innerWall.id) +
            ': ' +
            this.translateService.instant('WALL.' + innerWall.placedOnSide),
          value:
            this.translateService.instant(innerWallType.toUpperCase()) +
            '\n' +
            this.numberToString(this.getLeft(innerWall)) +
            ' mm ' +
            this.translateService.instant('SUMMARY.FROMLEFTCORNER') +
            '\n' +
            this.translateService.instant(innerWall.rotated ? 'STEPINNERWALL.MIRRORED' : 'STEPINNERWALL.NOTMIRRORED'),
        } as StepItem;
      });
      height = this.pdfGenerationService.generateStep(
        doc,
        null,
        innerWallsInPdf,
        height + marginBetweenSteps,
        'STEPINNERWALL.SUMMARYTITLE'
      );
    }
    const wallFinishesForPdf: StepItem[] = [];

    this.wallFinishes().forEach(wallFinish => {
      wallFinishesForPdf.push({
        title: this.getFinishTitle(wallFinish, false),
        value:
          this.translateService.instant('FINISHTYPES1.' + wallFinish.exterior) +
          ', ' +
          this.translateService.instant('FINISHTYPES2.' + wallFinish.exterior),
      } as StepItem);

      wallFinishesForPdf.push({
        title: this.getFinishTitle(wallFinish, true),
        value:
          this.translateService.instant('FINISHTYPES1.' + wallFinish.interior) +
          ', ' +
          this.translateService.instant('FINISHTYPES2.' + wallFinish.interior),
      } as StepItem);
    });
    this.innerWalls.forEach(iw => {
      wallFinishesForPdf.push({
        title:
          this.translateService.instant('WALL.4') +
          ' ' +
          this.getId(iw.id) +
          ' ' +
          this.translateService.instant('WALL.EXTERIOR'),
        value:
          this.translateService.instant('FINISHTYPES1.' + iw.exteriorFinish) +
          ', ' +
          this.translateService.instant('FINISHTYPES2.' + iw.exteriorFinish),
      } as StepItem);

      wallFinishesForPdf.push({
        title:
          this.translateService.instant('WALL.4') +
          ' ' +
          this.getId(iw.id) +
          ' ' +
          this.translateService.instant('WALL.INTERIOR'),
        value:
          this.translateService.instant('FINISHTYPES1.' + iw.interiorFinish) +
          ', ' +
          this.translateService.instant('FINISHTYPES2.' + iw.interiorFinish),
      } as StepItem);
    });
    height = this.pdfGenerationService.generateStep(
      doc,
      null,
      wallFinishesForPdf,
      height + marginBetweenSteps,
      'STEP6.SUMMARYTITLE'
    );

    this.pdfGenerationService.generateAdditionalImages(doc, imageInside, doorImages, height + marginBetweenSteps);

    this.pdfGenerationService.generateFooter(doc);
    const uri = doc.output('datauristring', {
      filename: this.translateService.instant('SUMMARY.YOURCONFIGURATION') + '.pdf',
    });

    const link = document.createElement('a');
    link.href = uri;
    link.download = this.translateService.instant('SUMMARY.YOURCONFIGURATION') + '.pdf';
    link.click();
    this.loadingPDF = false;
    return doc;
  }

  getCollisionsForPdf(): StepItem[] {
    const list = this.conflicts.invalidPanels.map(ip => {
      return {
        title: this.translateService.instant('INVALID_PANEL.TITLE').replace('[SIZE]', ip.size),
        value: null,
      };
    });
    list.push(
      ...this.conflicts.collisions.map(cp => {
        return {
          title: this.getTitleForCollision(cp),
          value: this.getATextForCollision(cp) + '\n' + this.getBTextForCollision(cp),
        };
      })
    );
    return list;
  }

  getTitleForCollision(collisionPoint: CollisionPoint): string {
    return (
      this.getADescriptionForCollision(collisionPoint) +
      ' ' +
      this.translateService.instant('AND') +
      ' ' +
      this.getBDescriptionForCollision(collisionPoint)
    );
  }

  getADescriptionForCollision(collisionPoint: CollisionPoint): string {
    let text = '';
    if (collisionPoint.meshAIsDoor) {
      text +=
        this.translateService.instant('DOOR') +
        ' ' +
        this.translateService.instant('WALL.' + collisionPoint.doorA.side);
      if (collisionPoint.doorA.side === WallSide.inner) {
        text += ` ${this.getId(collisionPoint.doorA.innerWallId)}`;
      }
    } else {
      text += this.translateService.instant('WALL.' + collisionPoint.wallA.side);
      if (collisionPoint.wallA.side === WallSide.inner) {
        text += ` ${this.getId(collisionPoint.wallA.innerWall.id)}`;
      }
    }
    return text;
  }

  getBDescriptionForCollision(collisionPoint: CollisionPoint): string {
    let text = '';
    if (collisionPoint.meshBIsDoor) {
      text +=
        this.translateService.instant('DOOR') +
        ' ' +
        this.translateService.instant('WALL.' + collisionPoint.doorB.side);
      if (collisionPoint.doorB.side === WallSide.inner) {
        text += ` ${this.getId(collisionPoint.doorB.innerWallId)}`;
      }
    } else {
      text += this.translateService.instant('WALL.' + collisionPoint.wallB.side);
      if (collisionPoint.wallB.side === WallSide.inner) {
        text += ` ${this.getId(collisionPoint.wallB.innerWall.id)}\n`;
        text += `${this.translateService.instant('WALL.' + collisionPoint.wallB.innerWall.placedOnSide)}`;
      }
    }
    return text;
  }

  getATextForCollision(collisionPoint: CollisionPoint): string {
    let text = '';
    if (collisionPoint.meshAIsDoor) {
      text += this.translateService.instant('DOOR');
      text += ` ${this.numberToString(collisionPoint.doorA.left)} mm ${this.translateService.instant(
        'SUMMARY.FROMLEFTCORNER'
      )}`;
    } else {
      text += this.translateService.instant('WALL.' + collisionPoint.wallA.side);
      if (collisionPoint.wallA.side === WallSide.inner) {
        text += ` ${this.getId(collisionPoint.wallA.innerWall.id)}`;
        text += '\n';
        text += `${this.translateService.instant(
          'WALL.' + collisionPoint.wallA.innerWall.placedOnSide
        )} ${this.numberToString(collisionPoint.wallA.innerWall.left)} mm ${this.translateService.instant(
          'SUMMARY.FROMLEFTCORNER'
        )}`;
      }
    }
    return text;
  }

  getBTextForCollision(collisionPoint: CollisionPoint): string {
    let text = '';
    if (collisionPoint.meshBIsDoor) {
      text += this.translateService.instant('DOOR');
      text += ` ${this.numberToString(collisionPoint.doorB.left)} mm ${this.translateService.instant(
        'SUMMARY.FROMLEFTCORNER'
      )}`;
    } else {
      text += this.translateService.instant('WALL.' + collisionPoint.wallB.side);
      if (collisionPoint.wallB.side === WallSide.inner) {
        text += ` ${this.getId(collisionPoint.wallB.innerWall.id)}\n`;
        text += `${this.translateService.instant(
          'WALL.' + collisionPoint.wallB.innerWall.placedOnSide
        )} ${this.numberToString(collisionPoint.wallB.innerWall.left)} mm ${this.translateService.instant(
          'SUMMARY.FROMLEFTCORNER'
        )}`;
      }
    }
    return text;
  }

  getFinishTitle(wallFinish: WallFinish, interior: boolean): string {
    let text = '';
    if (wallFinish.side === WallSide.ceiling) {
      text = this.translateService.instant('WALL.' + wallFinish.side) + ' ';
    } else {
      text =
        this.translateService.instant('SUMMARY.WALL') +
        ' ' +
        this.translateService.instant('WALL.' + wallFinish.side) +
        ' ';
    }
    if (interior) {
      text += this.translateService.instant('WALL.INTERIOR');
    } else {
      text += this.translateService.instant('WALL.EXTERIOR');
    }
    return text;
  }

  getId(id: string) {
    if (!id) {
      return '';
    }
    const names = id.split('-');
    if (names.length > 1) {
      return +names[1] + 1;
    }
    return '';
  }

  getLeft(iw: InnerWall) {
    return iw.left;
  }

  async create2dPlan() {
    if (this.loading2d) {
      return;
    }
    this.loading2d = true;
    this.configurationService.generate2dPlan(this.configurationService.configuration).subscribe(
      blob => {
        try {
          const downloadLink = document.createElement('a');
          downloadLink.href = window.URL.createObjectURL(blob.body);
          downloadLink.setAttribute('download', `${this.translateService.instant('SUMMARY.YOUR2DPLAN')}.pdf`);
          document.body.appendChild(downloadLink);
          downloadLink.click();
          setTimeout(() => downloadLink.parentNode.removeChild(downloadLink));
        } catch {
          this.notificationService.showNotification('SUMMARY.2DPLANNOTGENERATED', NotificationService.DEFAULT_TIMEOUT);
        } finally {
          this.loading2d = false;
        }
      },
      () => {
        this.notificationService.showNotification('SUMMARY.2DPLANNOTGENERATED', NotificationService.DEFAULT_TIMEOUT);
        this.loading2d = false;
      }
    );
  }

  getWidthOfWall(door: DoorPosition) {
    return this.threeDService.getWallBySide(door.side, door.innerWallId)?.wallLength * 1000;
  }
}
